import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmationDialogContext } from "shared/components/modal/confirm/ConfirmationDialogContext";
import useAwaitableComponent from "shared/hooks/useAwaitableComponent";
import { useBoolean } from "usehooks-ts";

export interface ConfirmationDialogAlertProps {
  onCloseComplete: () => void;
  onClose: () => void;
  isOpen: boolean;
}

export interface ConfirmationDialogProps<
  TResult,
  TProps = undefined,
  TArgs = undefined,
> {
  alertProps: ConfirmationDialogAlertProps;
  onSuccess: (result: TResult) => void;
  onCancel: (error?: unknown) => void;
  extraProps: TProps;
  args: TArgs;
}

type ExtraPropsFromDialogProps<Type> =
  Type extends React.ComponentType<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ConfirmationDialogProps<any, infer U, any>
  >
    ? U
    : never;

type OptionalOrRequiredArg<TArgs> = TArgs extends undefined
  ? []
  : [args: TArgs];

type ConfirmationDialogStartFn<TArgs, TResult> = (
  ...args: OptionalOrRequiredArg<TArgs>
) => Promise<TResult | undefined>;

export const useConfirmationDialog = <TResult, TExtraProps, TArgs = undefined>(
  DialogComponent: React.ComponentType<
    ConfirmationDialogProps<TResult, TExtraProps, TArgs>
  >,
  // this makes the argument optional
  // if the dialog component doesn't have extra props
  ...[extraProps]: TExtraProps extends undefined
    ? []
    : [ExtraPropsFromDialogProps<typeof DialogComponent>]
): ConfirmationDialogStartFn<TArgs, TResult> => {
  const dialogId = useId();

  const { addDialogElement, removeDialogElement } = useContext(
    ConfirmationDialogContext,
  );

  const [args, setArgs] = useState<TArgs>();

  const { isActive, handleResolve, handleReject, start } =
    useAwaitableComponent<TResult>();

  // stash the result/error so the callback can be called
  // after the dialog is closed completely
  const resultRef = useRef<TResult | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  const errorRef = useRef<unknown | undefined>(undefined);

  const {
    value: isOpen,
    setFalse: setClosed,
    setTrue: resetClosed,
  } = useBoolean(true);

  const onStart = useCallback(
    (args?: TArgs) => {
      setArgs(args);
      resetClosed();
      return start();
    },
    [resetClosed, start],
  );

  const onCloseComplete = useCallback(() => {
    if (resultRef.current !== undefined) {
      handleResolve(resultRef.current);
    } else {
      handleReject(errorRef.current);
    }
    setArgs(undefined);
    resultRef.current = undefined;
    errorRef.current = undefined;
  }, [handleReject, handleResolve]);

  const onSuccess = useCallback(
    (result: TResult) => {
      resultRef.current = result;
      errorRef.current = null;
      setClosed();
    },
    [setClosed],
  );

  const onCancel = useCallback(
    (error?: unknown) => {
      resultRef.current = undefined;
      errorRef.current = error;
      setClosed();
    },
    [setClosed],
  );

  const extraPropsRef = useRef(extraProps);
  extraPropsRef.current = extraProps;

  const element = useMemo(
    () =>
      isActive ? (
        <DialogComponent
          extraProps={extraPropsRef.current!}
          onSuccess={onSuccess}
          onCancel={onCancel}
          args={args as TArgs}
          alertProps={{
            onClose: setClosed,
            onCloseComplete,
            isOpen,
          }}
        />
      ) : null,
    [
      isActive,
      DialogComponent,
      onSuccess,
      onCancel,
      args,
      setClosed,
      onCloseComplete,
      isOpen,
    ],
  );

  useEffect(() => {
    addDialogElement(dialogId, element);
    return () => {
      removeDialogElement(dialogId);
    };
  }, [element, addDialogElement, removeDialogElement, dialogId]);

  return onStart;
};
